import { Dayjs } from "dayjs"

import { type PersonConstructor } from "#classes/Person"
import {
  type CoreSkillLevel,
  type DigitalSkillLevel,
  SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import { type OldStudentEvaluation } from "#customTypes/composedTypes/evaluationTypes"
import { UploadFile } from "#customTypes/composedTypes/firestoreTypes"
import { type PortfolioMeta } from "#customTypes/composedTypes/portfolioTypes"
import {
  type FirebaseDocRef,
  ReactChild,
  type Uuid,
} from "#customTypes/globalTypes"
import {
  type Address,
  type Attendance,
  AttendanceClassSession,
  type DatabaseObject,
  type EnumValues,
  type FormData,
  IsoDate,
  type TableData,
} from "#customTypes/types"

import { StudentTask } from "./studentTaskTypes"

export enum StudentSkillType {
  core = "core",
  digital = "digital",
  sculpture = "sculpture",
}

export interface StudentSkillHistory {
  [semester: string]: {
    [StudentSkillType.core]?: CoreSkillLevel
    [StudentSkillType.digital]?: DigitalSkillLevel
    [StudentSkillType.sculpture]?: SculptureSkillLevel
    coreEvalId?: Uuid
    digitalEvalId?: Uuid
    sculptureEvalId?: Uuid
  }
}

/** One unshared message compared with notes already sent to the family. */
export type FamilyRepeatVerdict = {
  momentId: string
  repeats: boolean
  /** Already-sent moment ids that are too close. Empty when repeats is false. */
  similarMomentIds: string[]
  reason: string
}

/**
 * Cached family-repeat check. `key` is the messages that were judged and
 * is what a reload compares. `momentIds` is the same set without note
 * text, and is what coverage uses.
 */
export type FamilyRepeatJudgment = {
  key: string
  momentIds?: string[]
  judgedAt: IsoDate
  source: "model" | "stand-in"
  messages: FamilyRepeatVerdict[]
}

export type StudentConstructor = DatabaseObject & {
  id: Uuid
  firstName: string
  lastName: string
  avatar: string
  name: string
  dob: string
  gender: string
  /**
   * Every skill level the student has, keyed by the semester it applies to.
   * The record of truth — resolve one with `getSkillLevelAsOf`
   * (utils/studentSkillHelpers), never by reaching in directly.
   */
  skillHistory?: StudentSkillHistory
  /**
   * Legacy levels with no semester attached. **Never read or written** —
   * every level resolves from `skillHistory` through `getSkillLevelAsOf`.
   *
   * They were a denormalized "current level" that only the forms set: an
   * approved promotion never touched them, and nothing refreshed them at a
   * semester boundary, so they drifted. They stay on the document so a
   * revert has something to read; `dbconversion/
   * backfillSkillHistoryFromFlatFields.ts` copies them into history for
   * ladders that have none.
   */
  coreSkillLevel?: CoreSkillLevel
  digitalSkillLevel?: DigitalSkillLevel
  sculptureSkillLevel?: SculptureSkillLevel
  primaryAddress: Address
  personalPhone: string
  personalEmail: string
  guardian1Name: string
  guardian1Phone: string
  guardian1Email: string
  guardian2Name?: string
  guardian2Phone?: string
  guardian2Email?: string
  notes?: string
  tags?: string[]
  allergies?: string[]
  // Attendance fields can be an explicit null: cleanFormData converts empty
  // objects to null on save (Firestore convention for clearing a map field)
  attendance?: Attendance | null
  assistantAttendance?: Attendance | null
  tasks?: StudentTask[]
  projects?: Record<Uuid, number> // How many days they've completed. By projectId.
  evals?: Record<Uuid, OldStudentEvaluation> // By evalId.
  artwork?: Record<Uuid, StudentArtwork>
  /**
   * Competition entries as slot (box) arrays, keyed by opaque competitionId.
   * Index = box position, value = the artworkId occupying it, null = empty
   * (arrays are null-padded when a high index is written). A piece occupies
   * at most one box per competition. This is the canonical membership record
   * — the per-piece `competitionIds` / `competitionId` on `StudentArtwork`
   * are the legacy shape. Written only by the mobile API.
   *
   * Keep in sync with server StudentConstructor in serverTypes.ts.
   */
  competitionSubmissions?: Record<string, Array<Uuid | null>>
  /**
   * Saved check of this student's unshared messages against notes already
   * sent to the family. Standout Review reads it on load so a reload does
   * not call the model again. Saving one of this student's moments drops
   * the field. Keep in sync with server StudentConstructor.
   */
  familyRepeatJudgment?: FamilyRepeatJudgment
  receipts?: Record<Uuid, StudentReceipt> // Receipt data per class enrollment
  files?: {
    waiver?: UploadFile // Release waiver (overwrites on update)
    tryoutEvals?: Record<Uuid, UploadFile> // keyed by tryoutId
  }
}

// Stringify complex fields like address for display
export type StudentForTable = TableData &
  Omit<
    StudentConstructor,
    | "primaryAddress"
    | "tags"
    | "allergies"
    | "attendance"
    | "assistantAttendance"
    | "skillHistory"
    | "coreSkillLevel"
    | "digitalSkillLevel"
    | "sculptureSkillLevel"
  > & {
    id: Uuid
    name: string
    primaryAddress: string
    skillLevels: string
    tags?: ReactChild
    allergies?: ReactChild
  }

/**
 * Row shape for surfaces that show levels. Resolved as of now by
 * `Student.getStudentForDisplay`, never stored.
 */
export interface StudentForDisplay extends TableData {
  id: Uuid
  avatar: string
  name: string
  dob: string
  coreSkillLevel: CoreSkillLevel | ""
  digitalSkillLevel: DigitalSkillLevel | ""
  sculptureSkillLevel: SculptureSkillLevel | ""
  tags: string
  allergies: string
}

export type PersonRef = FirebaseDocRef<PersonConstructor>
export type StudentRef = FirebaseDocRef<StudentConstructor>

export interface AddStudentFormValues extends FormData {
  id: Uuid | undefined
  firstName: string | undefined
  lastName: string | undefined
  name: string | undefined
  avatar: string | undefined
  dob: string | undefined
  gender: string | undefined
  coreSkillLevel?: CoreSkillLevel
  digitalSkillLevel?: DigitalSkillLevel
  sculptureSkillLevel?: SculptureSkillLevel
  /** Legacy field. Not collected by the add/edit form and not on StudentConstructor. */
  threeDSkillLevel?: string
  skillHistory?: StudentSkillHistory
  primaryAddress: Address | undefined
  personalPhone: string | undefined
  personalEmail: string | undefined
  guardian1Name: string | undefined
  guardian1Phone: string | undefined
  guardian1Email: string | undefined
  guardian2Name?: string
  guardian2Phone?: string
  guardian2Email?: string
  notes?: string
  tags?: string[]
  allergies?: string[]
}

export interface AddStudentFormSubmitValues extends AddStudentFormValues {
  id: Uuid | undefined
  createdAt: string | undefined
  lastUpdatedAt: string | undefined
  coreSkillLevel: CoreSkillLevel
  digitalSkillLevel: DigitalSkillLevel
  sculptureSkillLevel: SculptureSkillLevel
  primaryAddress: Address
  skillHistory: StudentSkillHistory
  personalPhone: string
  personalEmail: string
  guardian1Name: string | undefined
  guardian1Phone: string | undefined
  guardian1Email: string | undefined
  guardian2Name: string | undefined
  guardian2Phone: string | undefined
  guardian2Email: string | undefined
  notes: string | undefined
  tags: string[]
  allergies: string[]
}

export type AttendanceClassSessionWithDate = AttendanceClassSession & {
  sessionDate: Dayjs
}

export type StudentClassAttendance = {
  classId: Uuid
  isSummerCamp: boolean
  startDate: string
  name: string
  attendanceSessions: AttendanceClassSessionWithDate[]
}

/**
 * Which client wrote this artwork entry. Independent of `createdBy` (who
 * saved) and of `storageOrigin` (which Storage bucket). Absent on legacy
 * desktop/scanner records.
 *
 * Keep in sync with server `ARTWORK_CAPTURED_BY` in serverTypes.ts.
 */
export const ARTWORK_CAPTURED_BY = {
  PARENT: "parent",
  STUDENT: "student",
  TEACHER_APP: "teacherApp",
  SCANNER_APP: "scannerApp",
} as const

export type ArtworkCapturedBy = EnumValues<typeof ARTWORK_CAPTURED_BY>

export interface StudentArtwork {
  id: Uuid
  semester: string
  classId: Uuid
  projectId?: Uuid
  /**
   * When `true`, the portfolio backfill in `ensurePortfolioExists` owns this
   * artwork. Set to `false` to explicitly opt this piece OUT (e.g. after the
   * teacher deletes the stage/project that referenced it). `undefined` means
   * "not yet evaluated" — the backfill will flip to `true` on first open of a
   * portfolio-class artwork.
   */
  inPortfolio?: boolean
  sortIndex?: number
  filename: string
  path: string
  timeCreated: number
  url: string
  rotation?: number // Rotation in degrees (0, 90, 180, 270)
  portfolioMeta?: PortfolioMeta
  // Keep in sync with server StudentArtwork in serverTypes.ts.
  thumbnailUrl?: string
  thumbnailPath?: string
  /**
   * Mobile crop/perspective-corrected image URL. When present, every display
   * surface prefers it over `url`; `url` (the original) is never replaced.
   *
   * ROTATION: `rotation` applies UNIFORMLY at display time to whichever image
   * renders (crop or original) — staff can still fix a mis-rotated crop via
   * the existing desktop rotate workflow. Because the crop's pixels are
   * exactly what the user saw at save time (the mobile editor bakes any
   * in-editor rotation), SAVING a crop resets `rotation` to 0 (field removed;
   * absent displays identically to 0) so a stale rotation from the original
   * can't double-rotate the crop. Reverting a crop leaves `rotation` alone.
   */
  cropUrl?: string
  // Storage object path for cropUrl (mobile bucket). Paired with cropUrl.
  cropPath?: string
  storageOrigin?: "mobile" // absent = jarvis bucket (scanner/desktop/teacher-app upload)
  /**
   * Who saved this entry. Same convention as desktop audit fields: the
   * actor uid, not authorship. Scanner kiosk = the iPad Scanner user;
   * teacher app = PIN teacher or selected TA student; mobile = student id
   * or `g_`-prefixed guardian. Absent on legacy scanner/desktop records.
   */
  createdBy?: Uuid
  /**
   * Live class session whose roster contained the student (or the session
   * the teacher had selected). First match when a student is in overlapping
   * classes. Capture leaves it off when unknown. Quality Check may fill it
   * when sharing, from the latest session of `classId` that started at or
   * before `timeCreated`.
   */
  classSessionId?: Uuid
  /**
   * Which client captured the image. Distinguishes sources whose createdBy
   * namespaces overlap (a student id can be a mobile upload or a TA scan).
   */
  capturedBy?: ArtworkCapturedBy
  /**
   * Teacher-app writes `false` on new scans. No consumer currently filters
   * on it; missing on legacy docs means already live.
   */
  isApproved?: boolean
  title?: string
  medium?: string
  dimensions?: string
  /**
   * Competitions this piece has been entered in, as opaque server-side ids
   * (e.g. "FamilyFA26"). Display names live in a server constant, never here.
   */
  competitionIds?: string[]
  /**
   * Pre-array shape: a piece could only enter one competition. Superseded by
   * `competitionIds` within a single release, so no backfill was run — read
   * through `readCompetitionIds` on the server, which folds this in. Do not write.
   */
  competitionId?: string
  // School-awarded placement (e.g. "Finalist", "1st Place"). Set by staff
  // tooling, never by the mobile create endpoint.
  competitionOutcome?: string
  /**
   * Nondestructive delete for mistaken uploads (bad scans, dupes,
   * misassignment). `true` hides the piece from every surface — profile tab
   * (unless "Show hidden" is on), Quality Check, the wall, the mobile API,
   * and portfolio auto-import — while the Storage object stays untouched.
   * Absent/false = live. Written from the profile Artwork tab and the
   * Quality Check hover action (both LEAD_TEACHER+); restore (profile tab
   * only) sets `false` and leaves the stamps as history.
   */
  isHidden?: boolean
  /** Epoch ms of the last hide action. */
  hiddenAt?: number
  /** `currentUserId` of who hidden (audit — never the effective user). */
  hiddenBy?: Uuid
  /**
   * Epoch ms of the last saved crop, and who saved it. Same audit rule as
   * `hiddenBy`: the `currentUserId`, never the effective user. A revert
   * clears `cropUrl`/`cropPath` but leaves these as "last cropped" history,
   * so the work still counts.
   */
  croppedAt?: number
  croppedBy?: Uuid
  /**
   * When true, this scan is on the parent's class-day card for
   * `classSessionId`. Missing or false means not on that card. The profile
   * gallery does not read this flag. A hidden or don't-share piece is never
   * sent even when this is true. Quality Check and Standout Review write it; both require
   * UPDATE on `STANDOUT_REVIEW_PAGE`. A piece with no `classSessionId` cannot
   * be shared. Keep in sync with server `StudentArtwork`.
   */
  isShared?: boolean
  /**
   * When this scan was last shared onto the parent card. Older shares omit
   * it; review history then uses `timeCreated`.
   */
  sharedAt?: IsoDate
  /**
   * `currentUserId` of who last shared it (audit — never the effective user).
   * Unsharing only flips `isShared` and leaves this, matching how standout
   * moments keep `sharedBy` through an unshare.
   */
  sharedBy?: Uuid
  /**
   * Don't share (Standout Review). True keeps the piece off every family
   * surface: the class-day card, the mobile artwork list, and family
   * delivery. It stays everywhere in Jarvis, unlike `isHidden`, and
   * clearing it lets staff share again. Keep in sync with server
   * `StudentArtwork`.
   */
  dontShare?: boolean
  /** Why staff chose Don't share. Shown while `dontShare` is on. */
  dontShareReason?: string
}

export interface StudentReceipt {
  classId: Uuid
  optOutDates?: string
  paymentAmount?: string
  siblingDiscount: boolean
  extraClassDiscount: boolean
  createdAt: IsoDate
}
